import { generationActionGuard } from './generation-action-guard.js';
import { useState, type ReactNode } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import { Switch } from '@/components/ui/switch.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import type {
  RunExecutionMode,
  WebNetworkCaptureMode,
  WebNetworkCaptureOverride,
} from '@/lib/api/quality-types.js';

const PROJECT_DEFAULT_CAPTURE_VALUE = 'project-default';
const INHERITED_EXECUTION_VALUE = 'inherited';

const EXECUTION_MODE_LABELS: Record<RunExecutionMode, string> = {
  parallel: 'Parallel',
  sequential: 'One case at a time',
};

/** A newer service may answer with a mode this build does not know; treat it as the default. */
export function normalizeRunExecutionMode(value: unknown): RunExecutionMode {
  return value === 'sequential' ? 'sequential' : 'parallel';
}

/** Per-run case execution order. Without `onChange` the inherited value is shown read-only. */
export interface ExecutionOrderControl {
  inherited: RunExecutionMode;
  /** Null inherits; a mode is an explicit override for this run only. */
  value: RunExecutionMode | null;
  onChange?: (value: RunExecutionMode | null) => void;
}

export function RunAdvancedSettings({
  inheritedTakeoverEnabled = false,
  inheritanceLabel = 'workspace',
  executionInheritanceLabel,
  value = null,
  onChange,
  disabled = false,
  explainUnavailableActions = false,
  webNetworkCapture,
  executionOrder,
  variables,
  inline = false,
  executionFooter,
}: {
  inline?: boolean;
  executionFooter?: ReactNode;
  inheritedTakeoverEnabled?: boolean;
  inheritanceLabel?: string;
  executionInheritanceLabel?: string;
  value?: boolean | null;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  explainUnavailableActions?: boolean;
  webNetworkCapture?: {
    value: WebNetworkCaptureOverride;
    onChange: (value: WebNetworkCaptureOverride) => void;
    defaultLabel?: string;
    disabled?: boolean;
  };
  executionOrder?: ExecutionOrderControl;
  /** The launch's per-run variable overrides, shown as the last section when given. */
  variables?: ReactNode;
}) {
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const effectiveTakeoverEnabled = value ?? inheritedTakeoverEnabled;
  const executionControl = executionOrder ? (
    <div
      className={
        inline
          ? 'grid grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-x-3 gap-y-2'
          : 'flex flex-col gap-2'
      }
    >
      <span className="text-sm font-medium text-foreground">
        Case execution
      </span>
      {executionOrder.onChange ? (
        <Select
          disabled={!explainUnavailableActions && disabled}
          value={executionOrder.value ?? INHERITED_EXECUTION_VALUE}
          onValueChange={(selected) =>
            executionOrder.onChange?.(
              selected === INHERITED_EXECUTION_VALUE
                ? null
                : (selected as RunExecutionMode),
            )
          }
        >
          <SelectTrigger
            {...generationActionGuard(
              explainUnavailableActions && disabled,
              'Case execution settings are unavailable until a compatible Quality service is connected.',
            )}
            aria-label="Case execution"
            className={inline ? 'w-full min-w-0' : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERITED_EXECUTION_VALUE}>
              Inherited from {executionInheritanceLabel ?? inheritanceLabel}:{' '}
              {
                EXECUTION_MODE_LABELS[
                  normalizeRunExecutionMode(executionOrder.inherited)
                ]
              }
            </SelectItem>
            <SelectItem value="parallel">
              {EXECUTION_MODE_LABELS.parallel}
            </SelectItem>
            <SelectItem value="sequential">
              {EXECUTION_MODE_LABELS.sequential}
            </SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-muted-foreground">
          Inherited from {inheritanceLabel}:{' '}
          {
            EXECUTION_MODE_LABELS[
              normalizeRunExecutionMode(executionOrder.inherited)
            ]
          }
          .
        </p>
      )}
      {!inline && (
        <p className="text-xs text-muted-foreground">
          One case at a time starts the next case only after the previous one
          has finished, in suite order. Parallel runs every case as soon as a
          worker is free.
        </p>
      )}
      {executionFooter && (
        <div
          className={inline ? 'col-start-2 max-[600px]:col-start-1' : undefined}
        >
          {executionFooter}
        </div>
      )}
    </div>
  ) : null;
  return (
    <Collapsible
      open={inline || advancedSettingsOpen}
      onOpenChange={setAdvancedSettingsOpen}
      className={inline ? '' : 'rounded-lg border border-border'}
    >
      {!inline && (
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted/30">
          <span>Advanced settings</span>
          <ChevronDownIcon
            className={`size-4 text-muted-foreground transition-transform ${
              advancedSettingsOpen ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </CollapsibleTrigger>
      )}
      <CollapsibleContent
        className={inline ? '' : 'border-t border-border px-4 py-3'}
      >
        <div className={inline ? 'flex flex-col gap-2' : 'flex flex-col gap-4'}>
          {inline && executionControl && (
            <section className="space-y-2 border-t pt-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Execution
              </h4>
              {executionControl}
            </section>
          )}
          {onChange ? (
            <div className="flex flex-col gap-2">
              <label
                className={
                  inline
                    ? 'grid min-h-9 grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-3'
                    : 'flex items-center justify-between gap-4'
                }
              >
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    Human-in-the-loop
                  </span>
                  {!inline && (
                    <span className="text-xs text-muted-foreground">
                      Inherited from {inheritanceLabel}:{' '}
                      {inheritedTakeoverEnabled ? 'On' : 'Off'}. An explicit
                      choice here overrides that default for this run only.
                    </span>
                  )}
                </span>
                <Switch
                  className={inline ? 'justify-self-start' : undefined}
                  disabled={!explainUnavailableActions && disabled}
                  {...generationActionGuard(
                    explainUnavailableActions && disabled,
                    'Human-in-the-loop is unavailable until a compatible Quality service is connected.',
                  )}
                  checked={effectiveTakeoverEnabled}
                  onCheckedChange={onChange}
                  aria-label="Human-in-the-loop"
                />
              </label>
              {disabled && !inline && (
                <p className="text-xs text-muted-foreground">
                  This setting is unavailable until a compatible Quality service
                  is connected.
                </p>
              )}
              {!inline && (
                <p className="text-xs text-muted-foreground">
                  Allow the run to ask for missing input or request video
                  takeover when it cannot continue.
                </p>
              )}
            </div>
          ) : null}
          {!inline && executionControl}
          {webNetworkCapture ? (
            <div
              className={
                inline
                  ? 'grid grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-x-3 gap-y-2'
                  : 'flex flex-col gap-2'
              }
            >
              <span className="text-sm font-medium text-foreground">
                Web network diagnostics
              </span>
              <Select
                disabled={
                  !explainUnavailableActions && webNetworkCapture.disabled
                }
                value={webNetworkCapture.value ?? PROJECT_DEFAULT_CAPTURE_VALUE}
                onValueChange={(selected) =>
                  webNetworkCapture.onChange(
                    selected === PROJECT_DEFAULT_CAPTURE_VALUE
                      ? null
                      : (selected as WebNetworkCaptureMode),
                  )
                }
              >
                <SelectTrigger
                  {...generationActionGuard(
                    explainUnavailableActions && webNetworkCapture.disabled,
                    'Web network diagnostics are unavailable until a compatible Quality service is connected.',
                  )}
                  aria-label="Web network diagnostics"
                  className={inline ? 'w-full min-w-0' : undefined}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PROJECT_DEFAULT_CAPTURE_VALUE}>
                    {webNetworkCapture.defaultLabel ?? 'Use project default'}
                  </SelectItem>
                  <SelectItem value="errors-only">Errors only</SelectItem>
                  <SelectItem value="full-har">Full HAR</SelectItem>
                </SelectContent>
              </Select>
              {!inline && (
                <p className="text-xs text-muted-foreground">
                  Errors only saves HTTP-error details and extracted messages.
                  Full HAR records all network requests and responses internally
                  and can use more memory. Neither mode publishes raw capture
                  files.
                </p>
              )}
            </div>
          ) : null}
          {variables ? (
            <div
              className={
                inline
                  ? 'flex flex-col gap-2 border-t pt-3'
                  : 'flex flex-col gap-2'
              }
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  Variables
                </span>
                <span className="text-xs text-muted-foreground">
                  {inline
                    ? 'Override values for this run.'
                    : 'The workspace variables these tests use. Switch one on to change its value for this run only.'}
                </span>
              </span>
              {variables}
            </div>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
